import type { FC, ReactNode } from 'react'
import type { Tone } from '@/constants/carwash'
import type {
    InvoiceStatus,
    JobStatus,
    StaffStatus
} from '@/types'

import { cn } from '@/lib/utils'
import { JOB_STATUS_META, STAFF_STATUS_META } from '@/constants/carwash'

export const TONE_BADGE: Record<Tone, string> = {
    orange: 'bg-orange-50 text-orange-700 ring-orange-600/20',
    violet: 'bg-violet-50 text-violet-700 ring-violet-600/20',
    blue: 'bg-sky-50 text-sky-800 ring-sky-600/20',
    teal: 'bg-teal-50 text-teal-800 ring-teal-600/20',
    green: 'bg-emerald-50 text-emerald-800 ring-emerald-600/20',
    slate: 'bg-slate-100 text-slate-700 ring-slate-500/20',
    amber: 'bg-amber-50 text-amber-800 ring-amber-600/25',
    red: 'bg-red-50 text-red-700 ring-red-600/20'
}

export const TONE_DOT: Record<Tone, string> = {
    orange: 'bg-orange-500',
    violet: 'bg-violet-500',
    blue: 'bg-sky-500',
    teal: 'bg-teal-500',
    green: 'bg-emerald-500',
    slate: 'bg-slate-400',
    amber: 'bg-amber-500',
    red: 'bg-red-500'
}

export const TONE_TEXT: Record<Tone, string> = {
    orange: 'text-orange-700',
    violet: 'text-violet-700',
    blue: 'text-sky-700',
    teal: 'text-teal-700',
    green: 'text-emerald-700',
    slate: 'text-slate-600',
    amber: 'text-amber-800',
    red: 'text-red-700'
}

interface BadgeProps {
    tone: Tone
    children: ReactNode
    dot?: boolean
    live?: boolean
    size?: 'sm' | 'md'
    className?: string
}

export const Badge: FC<BadgeProps> = ({
    tone,
    children,
    dot = false,
    live = false,
    size = 'sm',
    className
}): ReactNode => {
    return (
        <span
            className={cn(
                'inline-flex shrink-0 items-center gap-1.5 rounded-full font-semibold whitespace-nowrap ring-1 ring-inset',
                size === 'sm' ? 'h-6 px-2 text-xs' : 'h-7 px-2.5 text-[13px]',
                TONE_BADGE[tone],
                className
            )}
        >
            {dot && (
                <span
                    className={cn('size-1.5 rounded-full', TONE_DOT[tone], live && 'animate-live')}
                    aria-hidden='true'
                />
            )}

            {children}
        </span>
    )
}

export const JobStatusBadge: FC<{ status: JobStatus, size?: 'sm' | 'md', className?: string }> = ({ status, size, className }): ReactNode => {
    const meta = JOB_STATUS_META[status]

    return (
        <Badge tone={meta.tone} dot live={status === 'washing'} size={size} className={className}>
            {meta.label}
        </Badge>
    )
}

export const StaffStatusBadge: FC<{ status: StaffStatus, size?: 'sm' | 'md', className?: string }> = ({ status, size, className }): ReactNode => {
    const meta = STAFF_STATUS_META[status]

    return (
        <Badge tone={meta.tone} dot size={size} className={className}>
            {meta.label}
        </Badge>
    )
}

export const PaymentBadge: FC<{ status: InvoiceStatus, size?: 'sm' | 'md', className?: string }> = ({ status, size, className }): ReactNode => {
    return (
        <Badge tone={status === 'paid' ? 'green' : 'orange'} size={size} className={className}>
            {status === 'paid' ? 'Paid' : 'Pending'}
        </Badge>
    )
}
